/**
 * <ym-product>: Yallmark product page.
 *
 * - Personalized products: "Personalize" opens the popup (<ym-personalize>, sections/ym-personalize
 *   .liquid). After saving, a summary card with Edit replaces the button and Add to cart shows.
 *   Sizes are not needed to personalize; missing ones are reported on Add to cart.
 * - Set mode (custom.baby_product + custom.mama_product): "What you get" Set / Baby only /
 *   Mama only, one colour for both, a size per garment. A set adds two line items, each with
 *   the Name and Year properties and a shared hidden _Set id.
 * - Single products: Color swatches, Material cards and Size as a dropdown or pill buttons
 *   ([data-ym-opt]); optional Year choice and quantity (ornaments).
 * - Size chart dialog ([data-ym-sc], snippets/ym-size-chart.liquid): a tab per garment, IN/CM,
 *   rows as radios; "Select {size}" fills the page control and moves on to the next empty tab.
 * - Each line carries Name and Year, plus hidden _Print (the exact print text), and for a set
 *   _Set, _SetHandle and _Part, which the cart drawer uses to show the set as one row.
 * - "Edit personalization" in the cart drawer links here with ?ym_edit=<set id or line key>: the
 *   page restores the choices, opens the popup and, on save, updates those cart lines.
 * - Upsell (custom.upsell_product on a single personalized product): "Make it a set with …" with
 *   an Add toggle on the page and in the popup; when added, its size select shows, the total
 *   includes it and it goes in the cart as its own line, without a name.
 * - Validation on Add to cart lists everything still missing in one message.
 * - Mobile sticky bar mirrors the selection and price while the main button is off screen.
 * - Mobile gallery: swipe with thumbnails and a counter.
 * - Variant photo: when the chosen variant has its own featured media (variants[].media), the
 *   gallery shows it: mobile scrolls to its slide, desktop moves it up to the full-width spot.
 *   Variants without one (sizes) leave the gallery as it is. On load it follows ?variant=.
 * - App personalizer (config.app, Teeinblue): the app fills a real product form
 *   ([data-ym-app-form]), on the page or in its own popup. The page keeps its hidden variant id
 *   and ?variant= in step and checks the options before the app sees Add to cart or its
 *   Personalize button. Until the app's Personalize button (popup mode) shows, a Yallmark one
 *   ([data-ym-app-wait]) holds its place and opens the app's popup once it can; Add to cart stays
 *   hidden meanwhile. The app adds to cart itself and fires teeinblue-event-after-cart-added,
 *   which opens the cart drawer; a plain submit of the form is posted by AJAX as a fallback.
 *   Analytics: the app's Personalize is personalize_open, its add is personalize_save
 *   (assets/ym-track.js), as Open and Save of the Yallmark popups.
 *   The sticky button opens the app's popup, or scrolls to its form.
 * - Bundle and save ([data-ym-bo], snippets/ym-bundle-offer.liquid, set up by
 *   assets/ym-bundle.js): after an add, a popup over the cart drawer offers products of the same
 *   kind (same blank, custom.bundle_picks) and of other kinds (custom.bundle_mix_picks) with their
 *   price once added, while a bigger discount is still ahead (custom.bundle_tiers for the same
 *   kind, custom.bundle_mix for mix and match). The chip under the price
 *   (snippets/ym-bundle-chip.liquid) gives the offer; openBundleBrowse() opens the popup at any
 *   time. A card opens that product with ?ym_bundle=<this handle>: there the personalize popup opens when
 *   no option is missing, and the line gets the hidden property _Bundle=<handle> for reports.
 * - App in the Yallmark popup ([data-ym-app-dialog], Easify): Personalize opens the Yallmark popup
 *   holding the app's options; "What we'll print" lists them as typed. Save needs the confirm box
 *   and passes the app's own required check (TPOConfigs.actions.validateOptionSet), then the
 *   summary card, Edit and Add to cart show. Closing with changed options drops the saved state.
 */
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent } from '@shopify/events';
import { bundleNext, bundleOffer, fillBundleOffer } from '@theme/ym-bundle';
import { track } from '@theme/ym-track';

class YmProduct extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.shopify-section') || document;
    const configEl = section.querySelector('[data-ym-product-config]');
    if (!configEl) return;

    /** @type {any} */
    this.config = JSON.parse(configEl.textContent || '{}');
    this.state = {
      mode: this.config.mode === 'set' ? 'set' : 'single',
      tried: false,
      busy: false,
      qty: 1,
      /** @type {{ name: string, year: string } | null} */
      saved: null,
      upsell: false,
      /** @type {string[] | null} Lines of the saved app personalization (app in the Yallmark popup) */
      appSaved: null,
    };

    this.bind();
    this.initSizeChart();
    this.render();
    this.initSticky();
    this.initGallery();
    this.initVariantMedia();
    this.initEdit();
    this.initApp();
    this.initBundle();
  }

  /* ---------- helpers ---------- */

  /** @param {number} cents */
  money(cents) {
    return formatMoney(cents, this.config.moneyFormat || '${{amount}}', this.config.currency || 'USD');
  }

  /** @param {string} selector */
  $(selector) {
    return this.querySelector(selector);
  }

  /** @param {string} selector */
  $$(selector) {
    return Array.from(this.querySelectorAll(selector));
  }

  /** The personalize popup on this template, if any. */
  get pz() {
    return /** @type {any} */ (document.querySelector('ym-personalize'));
  }

  get swatch() {
    return /** @type {HTMLElement | null} */ (this.$('.ym-swatch[aria-pressed="true"]'));
  }

  get color() {
    return this.swatch?.dataset.ymColor || '';
  }

  get year() {
    return this.state.saved?.year || String(this.config.year || '');
  }

  /** Option controls of a single product: selects and pill/card groups. */
  get opts() {
    return /** @type {HTMLElement[]} */ (this.$$('[data-ym-opt]'));
  }

  /** @param {HTMLElement} el */
  optValue(el) {
    if (el instanceof HTMLSelectElement) return el.value;
    const pressed = /** @type {HTMLElement | null} */ (el.querySelector('[data-ym-choice][aria-pressed="true"]'));
    return pressed?.dataset.ymChoice || '';
  }

  /** Controls that must have a value for a part. @param {string} part */
  controls(part) {
    return part === 'single' ? this.opts : /** @type {HTMLElement[]} */ (this.$$(`[data-ym-size="${part}"]`));
  }

  /** Which garments are in play: ['baby','mama'] | ['baby'] | ['mama'] | ['single'] */
  get parts() {
    if (this.state.mode === 'single') return this.state.upsell ? ['single', 'upsell'] : ['single'];
    if (this.state.mode === 'baby') return ['baby'];
    if (this.state.mode === 'mama') return ['mama'];
    return ['baby', 'mama'];
  }

  /** Selected values for a product, keyed by lower-case option name. @param {string} part */
  selections(part) {
    /** @type {Record<string, string>} */
    const out = {};
    if (this.color) {
      out.color = this.color;
      out.colour = this.color;
    }
    for (const el of this.controls(part)) {
      out[(el.dataset.ymOptionName || '').toLowerCase()] = this.optValue(el);
    }
    // The upsell follows the page colour when it has it, else takes its own first colour.
    const product = this.product(part);
    if (part === 'upsell' && product) {
      product.options.forEach((/** @type {string} */ name, /** @type {number} */ i) => {
        const key = name.toLowerCase();
        if (key !== 'color' && key !== 'colour') return;
        const values = product.variants.map((/** @type {any} */ v) => v.options[i]);
        if (!values.includes(out[key])) out[key] = values[0];
      });
    }
    return out;
  }

  /** @param {string} part */
  product(part) {
    return this.config.products?.[part];
  }

  /** Exact variant for the current choices, or null. @param {string} part */
  variant(part) {
    const product = this.product(part);
    if (!product) return null;
    if (product.variants.length === 1) return product.variants[0];
    const chosen = this.selections(part);
    return (
      product.variants.find((/** @type {any} */ v) =>
        product.options.every((/** @type {string} */ name, /** @type {number} */ i) => {
          const want = chosen[name.toLowerCase()];
          // An option of one value has no control on the page (e.g. Teeinblue's "Available Product").
          if (want === undefined && product.variants.every((/** @type {any} */ o) => o.options[i] === v.options[i])) return true;
          return want !== undefined && want !== '' && v.options[i] === want;
        })
      ) || null
    );
  }

  /** Variants that fit what is chosen so far (unchosen options match anything). @param {string} part */
  matching(part) {
    const product = this.product(part);
    if (!product) return [];
    const chosen = this.selections(part);
    return product.variants.filter((/** @type {any} */ v) =>
      product.options.every((/** @type {string} */ name, /** @type {number} */ i) => {
        const want = chosen[name.toLowerCase()];
        return !want || v.options[i] === want;
      })
    );
  }

  /** Variant a part is priced by: exact variant, else the cheapest among variants matching what is chosen. @param {string} part */
  priceVariant(part) {
    const exact = this.variant(part);
    if (exact) return exact;
    const product = this.product(part);
    if (!product) return null;
    const pool = this.matching(part);
    return (pool.length ? pool : product.variants).reduce((/** @type {any} */ a, /** @type {any} */ b) => (b.price < a.price ? b : a));
  }

  /** @param {string} part */
  partPrice(part) {
    return this.priceVariant(part)?.price ?? 0;
  }

  /** Compare-at for a part, or its price when that variant has no real compare-at. @param {string} part */
  partCompare(part) {
    const v = this.priceVariant(part);
    return v ? Math.max(v.compare || 0, v.price) : 0;
  }

  get price() {
    if (this.config.mode === 'set') {
      if (this.state.mode === 'set') {
        // Only promise the set price when a discount actually applies it in the cart.
        return this.config.setDiscount ? this.config.setPrice : this.partPrice('baby') + this.partPrice('mama');
      }
      return this.partPrice(this.state.mode);
    }
    const upsell = this.state.upsell ? this.partPrice('upsell') : 0;
    return this.partPrice('single') * this.state.qty + upsell;
  }

  /** Same total as `price`, at compare-at prices. Equal to `price` when nothing is on sale. */
  get compareAt() {
    if (this.config.mode === 'set') {
      if (this.state.mode === 'set') {
        return this.config.setDiscount
          ? Math.max(this.config.setCompare || 0, this.config.setPrice)
          : this.partCompare('baby') + this.partCompare('mama');
      }
      return this.partCompare(this.state.mode);
    }
    const upsell = this.state.upsell ? this.partCompare('upsell') : 0;
    return this.partCompare('single') * this.state.qty + upsell;
  }

  /** Problems that block Add to cart, as sentences. Missing choices are listed in one. */
  get errors() {
    /** @type {string[]} */
    const missing = [];
    /** @type {string[]} */
    const other = [];
    for (const part of this.parts) {
      const controls = this.controls(part);
      for (const el of controls) {
        if (this.optValue(el)) continue;
        if (part === 'upsell') missing.push(`a size for the ${this.config.upsell?.label || 'second item'}`);
        else missing.push(part === 'single' ? `a ${(el.dataset.ymOptionName || 'option').toLowerCase()}` : `a ${part} size`);
      }
      if (controls.every((el) => this.optValue(el))) {
        const v = this.variant(part);
        if (!v) other.push(part === 'single' ? 'That option is not available. Choose another one.' : `That ${part} size is not available. Choose another one.`);
        else if (!v.available) other.push(part === 'single' ? 'That option is sold out.' : `That ${part} size is sold out.`);
      }
    }
    const errs = [];
    if (missing.length) {
      const list = missing.length > 1 ? `${missing.slice(0, -1).join(', ')} and ${missing.at(-1)}` : missing[0];
      errs.push(`Choose ${list} to ${this.config.mode === 'set' ? 'add to cart' : 'continue'}.`);
    }
    return [...errs, ...other];
  }

  get summary() {
    const parts = [];
    if (this.color) parts.push(this.color);
    if (this.config.mode === 'set') {
      for (const part of this.parts) {
        const select = /** @type {HTMLSelectElement | null} */ (this.$(`[data-ym-size="${part}"]`));
        const label = part === 'baby' ? 'Baby' : 'Mama';
        parts.push(select && select.value ? `${label} ${select.value}` : `${label} size —`);
      }
    } else {
      for (const el of this.opts) {
        const value = this.optValue(el);
        parts.push(value || `${el.dataset.ymOptionName || 'Option'} —`);
      }
    }
    if (this.config.personalized) {
      parts.push(this.state.saved ? this.state.saved.name : 'Name —');
      if (this.config.pickYear > 1 && this.state.saved) parts.push(this.state.saved.year);
    }
    if (this.config.quantity) parts.push(`Qty ${this.state.qty}`);
    if (this.state.upsell && this.config.upsell) {
      const size = this.controls('upsell').map((el) => this.optValue(el)).filter(Boolean).join(' ');
      return `${parts.join(' · ')} + ${this.config.upsell.label}${size ? ` ${size}` : ''}`;
    }
    return parts.join(' · ');
  }

  /* ---------- events ---------- */

  bind() {
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);

      // "Pairs with" card (snippets/ym-pairs-with.liquid): count the click, the link opens the partner
      const pair = target.closest('ym-pairs-with');
      if (pair instanceof HTMLElement && target.closest('[data-ym-pairs-btn]')) {
        const from = this.config.handle || '';
        this.publish('pairs_with_click', { product: from, from, to: pair.dataset.partnerHandle || '' });
        return;
      }

      const mode = target.closest('[data-ym-mode]');
      if (mode instanceof HTMLElement) {
        this.state.mode = mode.dataset.ymMode || 'set';
        this.$$('[data-ym-mode]').forEach((b) => {
          b.setAttribute('aria-checked', String(b === mode));
          b.setAttribute('tabindex', b === mode ? '0' : '-1');
        });
        this.hideAdded();
        this.render();
        return;
      }

      const swatch = target.closest('.ym-swatch');
      if (swatch instanceof HTMLElement) {
        this.$$('.ym-swatch').forEach((b) => b.setAttribute('aria-pressed', String(b === swatch)));
        this.hideAdded();
        this.render();
        return;
      }

      const choice = target.closest('[data-ym-choice]');
      const group = choice?.closest('[data-ym-opt]');
      if (choice instanceof HTMLElement && group instanceof HTMLElement) {
        group.querySelectorAll('[data-ym-choice]').forEach((b) => b.setAttribute('aria-pressed', String(b === choice)));
        this.hideAdded();
        this.render();
        return;
      }

      const qty = target.closest('[data-ym-qty]');
      if (qty instanceof HTMLElement) {
        this.state.qty = Math.min(20, Math.max(1, this.state.qty + Number(qty.dataset.ymQty)));
        this.hideAdded();
        this.render();
        return;
      }

      if (target.closest('[data-ym-upsell-toggle]')) {
        this.setUpsell(!this.state.upsell);
        return;
      }

      const pzOpen = target.closest('[data-ym-pz-open]');
      if (pzOpen instanceof HTMLElement) {
        this.openPersonalize(pzOpen);
        return;
      }

      if (target.closest('[data-ym-sc-open]')) {
        this.openSizeChart(/** @type {HTMLElement} */ (target.closest('[data-ym-sc-open]')));
        return;
      }

      if (target.closest('[data-ym-app-wait]')) {
        this.openAppWhenReady();
        return;
      }

      const appOpen = target.closest('[data-ym-app-open]');
      if (appOpen instanceof HTMLElement) {
        this.openAppPopup(appOpen);
        return;
      }

      if (target.closest('[data-ym-add]')) {
        const fromSticky = Boolean(target.closest('[data-ym-sticky]'));
        // App personalizer: the main button submits the form (initApp), the sticky one leads to it.
        if (this.config.app) {
          if (fromSticky) this.showApp();
          return;
        }
        this.add(fromSticky);
      }
    });

    // Arrow keys move between radios or tabs in a [data-ym-roving] group and select the new one.
    this.addEventListener('keydown', (event) => {
      const keys = ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      const target = /** @type {HTMLElement} */ (event.target);
      const group = target.closest('[data-ym-roving]');
      if (!group || !target.matches('[role="radio"], [role="tab"]')) return;
      const items = /** @type {HTMLElement[]} */ (
        Array.from(group.querySelectorAll('[role="radio"], [role="tab"]'))
      ).filter((el) => !el.hidden && el.getAttribute('aria-disabled') !== 'true');
      const i = items.indexOf(target);
      if (i < 0 || !items.length) return;
      event.preventDefault();
      let next = i;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = items.length - 1;
      else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (i + 1) % items.length;
      else next = (i - 1 + items.length) % items.length;
      items[next].focus();
      items[next].click();
    });

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement) {
        this.hideAdded();
        this.render();
      }
    });
  }

  hideAdded() {
    const added = this.$('[data-ym-added]');
    if (added instanceof HTMLElement) added.hidden = true;
  }

  /* ---------- render ---------- */

  render() {
    const { config, state } = this;
    const parts = this.parts;

    // Part visibility (set mode)
    if (config.mode === 'set') {
      this.$$('[data-ym-part]').forEach((el) => {
        /** @type {HTMLElement} */ (el).hidden = !parts.includes(/** @type {HTMLElement} */ (el).dataset.ymPart || '');
      });
      this.$('[data-ym-sizes]')?.classList.toggle('is-single', parts.length === 1);
      // One select: "Size · note" + fit hint, no label above it. Two: "Sizes" + Baby / Mama labels.
      const sizeKey = parts.length === 1 ? parts[0] : 'set';
      this.$$('[data-ym-size-head]').forEach((el) => {
        /** @type {HTMLElement} */ (el).hidden = /** @type {HTMLElement} */ (el).dataset.ymSizeHead !== sizeKey;
      });
      this.$$('[data-ym-size-hint]').forEach((el) => {
        /** @type {HTMLElement} */ (el).hidden = /** @type {HTMLElement} */ (el).dataset.ymSizeHint !== sizeKey;
      });
      this.$$('[data-ym-sizes] [data-ym-part] > .ym-size__label').forEach((el) => {
        el.classList.toggle('ym-visually-hidden', parts.length === 1);
      });
    } else {
      const sizes = /** @type {HTMLElement | null} */ (this.$('[data-ym-part="upsell"]'));
      if (sizes) sizes.hidden = !this.state.upsell;
    }

    // Colour name
    const colorName = this.$('[data-ym-color-name]');
    if (colorName && this.color) colorName.textContent = this.color;

    // Options
    for (const el of [...this.opts, ...this.$$('[data-ym-size]')]) {
      const control = /** @type {HTMLElement} */ (el);
      const visible = !control.closest('[hidden]');
      const value = this.optValue(control);
      control.setAttribute('aria-invalid', String(state.tried && visible && !value));
      const name = control.dataset.ymOptionName;
      if (name && !control.matches('select')) {
        const label = this.$(`[data-ym-opt-value="${CSS.escape(name)}"]`);
        if (label) label.textContent = value || 'choose';
      }
    }
    const qtyValue = this.$('[data-ym-qty-value]');
    if (qtyValue) qtyValue.textContent = String(state.qty);

    // Prices
    const price = this.money(this.price);
    this.$$('[data-ym-price]').forEach((el) => (el.textContent = price));

    // Sale: red price, compare-at struck through, "N% off" (rounded down). "Sale ends" is server-rendered.
    // The mobile sticky bar swaps its summary and price for the same sale block.
    const total = this.price;
    const compare = this.compareAt;
    const onSale = compare > total;
    this.$('[data-ym-price-row]')?.classList.toggle('ym-info__price-row--sale', onSale);
    this.$$('[data-ym-sale-label]').forEach((el) => ((/** @type {HTMLElement} */ (el)).hidden = !onSale));
    this.$$('[data-ym-compare]').forEach((el) => {
      /** @type {HTMLElement} */ (el).hidden = !onSale;
      el.textContent = onSale ? this.money(compare) : '';
    });
    this.$$('[data-ym-sale-line]').forEach((el) => ((/** @type {HTMLElement} */ (el)).hidden = !onSale));
    this.$$('[data-ym-no-sale]').forEach((el) => ((/** @type {HTMLElement} */ (el)).hidden = onSale));
    const offText = onSale ? `${Math.floor(((compare - total) * 100) / compare)}% off` : '';
    this.$$('[data-ym-sale-off]').forEach((el) => (el.textContent = offText));

    const note = this.$('[data-ym-set-note]');
    if (note instanceof HTMLElement) {
      const baby = this.partPrice('baby');
      const mama = this.partPrice('mama');
      const saving = config.setDiscount ? baby + mama - config.setPrice : 0;

      // "What you get" rows
      /** @type {Record<string, number>} */
      const modePrices = { set: config.setDiscount ? config.setPrice : baby + mama, baby, mama };
      this.$$('[data-ym-mode-price]').forEach((el) => {
        el.textContent = this.money(modePrices[/** @type {HTMLElement} */ (el).dataset.ymModePrice || 'set']);
      });
      const save = this.$('[data-ym-mode-save]');
      if (save instanceof HTMLElement) {
        save.hidden = saving <= 0;
        save.textContent = saving > 0 ? `Save ${this.money(saving)}` : '';
      }

      note.hidden = state.mode !== 'set';
      note.textContent =
        saving > 0
          ? `Baby ${this.money(baby)} + Mama ${this.money(mama)} separately · save ${this.money(saving)}`
          : `Baby ${this.money(baby)} + Mama ${this.money(mama)}`;
    }

    // Errors
    const errorEl = this.$('[data-ym-error]');
    if (errorEl instanceof HTMLElement) {
      const errs = this.errors;
      errorEl.hidden = !(state.tried && errs.length);
      if (errs.length) errorEl.textContent = errs.join(' ');
    }

    const summary = this.$('[data-ym-summary]');
    if (summary) summary.textContent = this.summary;

    this.renderPersonalized();
    this.renderApp();
    if (this.galleryMedia !== undefined) this.showVariantMedia(this.mediaVariant());
  }

  /* ---------- personalize ---------- */

  /** @param {boolean} on */
  setUpsell(on) {
    if (!this.config.upsell) return;
    this.state.upsell = on;
    this.hideAdded();
    this.render();
  }

  /** Colour swatches, or material cards (ornament), offered as choices in the popup. */
  get choiceEls() {
    const swatches = /** @type {HTMLElement[]} */ (this.$$('.ym-swatch'));
    if (swatches.length) return { label: 'Color', els: swatches, value: (/** @type {HTMLElement} */ el) => el.dataset.ymColor || '' };
    const cards = /** @type {HTMLElement[]} */ (this.$$('.ym-card-opt[data-ym-choice]'));
    return { label: 'Material', els: cards, value: (/** @type {HTMLElement} */ el) => el.dataset.ymChoice || '' };
  }

  /** Resolve "var(--token)" against this element, so the popup gets a real colour. @param {string} fill */
  resolveFill(fill) {
    const m = /^var\((--[\w-]+)\)$/.exec(fill || '');
    return m ? getComputedStyle(this).getPropertyValue(m[1]).trim() || fill : fill;
  }

  get choices() {
    const { els, value } = this.choiceEls;
    return els.map((el) => ({
      value: value(el),
      label: el.getAttribute('aria-label') || value(el),
      fill: this.resolveFill(el.dataset.fill || ''),
      dark: el.dataset.dark === 'true',
      selected: el.getAttribute('aria-pressed') === 'true',
    }));
  }

  /** @param {HTMLElement} opener */
  openPersonalize(opener) {
    const pz = this.pz;
    if (!pz || typeof pz.open !== 'function' || !pz.canPersonalize(this.parts)) {
      const errorEl = this.$('[data-ym-error]');
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = 'Personalization is not available right now. Please try again later.';
        errorEl.hidden = false;
      }
      return;
    }
    const { label, els, value } = this.choiceEls;
    pz.open({
      parts: this.parts,
      name: this.state.saved?.name || '',
      year: this.year,
      years: this.config.years || [],
      milestone: this.config.milestone || '',
      choices: this.choices,
      choiceLabel: label,
      product: this.config.handle || '',
      upsell: this.config.upsell
        ? { ...this.config.upsell, added: this.state.upsell, onToggle: (/** @type {boolean} */ on) => this.setUpsell(on) }
        : null,
      opener,
      onChoice: (/** @type {string} */ v) => els.find((el) => value(el) === v)?.click(),
      onSave: (/** @type {{ name: string, year: string }} */ saved) => {
        this.state.saved = saved;
        if (this.state.editing) {
          this.updateCartPersonalization(saved);
          return;
        }
        this.hideAdded();
        this.render();
        // Focus moves on to Add to cart, the next step.
        requestAnimationFrame(() => /** @type {HTMLElement | null} */ (this.$('.ym-buy > [data-ym-add]'))?.focus());
      },
    });
  }

  renderPersonalized() {
    if (!this.config.personalized) return;
    const saved = this.state.saved;
    const start = /** @type {HTMLElement | null} */ (this.$('[data-ym-pz-start]'));
    const card = /** @type {HTMLElement | null} */ (this.$('[data-ym-pz-card]'));
    const add = /** @type {HTMLElement | null} */ (this.$('.ym-buy > [data-ym-add]'));
    if (start) start.hidden = Boolean(saved);
    if (card) card.hidden = !saved;
    if (add) add.hidden = !saved;
    const stickyBtn = this.$('[data-ym-sticky-btn]');
    if (stickyBtn) stickyBtn.textContent = saved ? 'Add to cart' : 'Personalize';
    const upsell = /** @type {HTMLElement | null} */ (this.$('[data-ym-upsell]'));
    if (upsell) {
      upsell.hidden = !saved;
      upsell.querySelector('[data-ym-upsell-toggle]')?.setAttribute('aria-pressed', String(this.state.upsell));
      const label = upsell.querySelector('[data-ym-upsell-label]');
      if (label) label.textContent = this.state.upsell ? 'Added' : 'Add';
    }

    const pz = this.pz;
    if (!saved || !card || typeof pz?.lines !== 'function') return;
    const lines = pz.lines(this.parts, saved.name, saved.year, this.config.milestone || '');
    card.querySelector('[data-ym-pz-lines]')?.replaceChildren(
      ...lines.map((/** @type {{ label: string, text: string }} */ line) => {
        const row = document.createElement('div');
        const k = document.createElement('span');
        const v = document.createElement('span');
        k.textContent = `${line.label}: `;
        v.textContent = line.text;
        row.append(k, v);
        return row;
      })
    );
    const choice = this.choices.find((c) => c.selected);
    const preview = pz.thumbnail(this.parts, saved.name, saved.year, this.config.milestone || '', choice);
    card.querySelector('[data-ym-pz-thumb]')?.replaceChildren(...(preview ? [preview] : []));
  }

  /* ---------- add to cart ---------- */

  /** @param {boolean} fromSticky */
  async add(fromSticky) {
    if (this.state.busy) return;
    if (this.config.personalized && !this.state.saved) {
      this.openPersonalize(/** @type {HTMLElement} */ (this.$(fromSticky ? '[data-ym-sticky-btn]' : '[data-ym-pz-open]')));
      return;
    }
    const errs = this.errors;
    if (errs.length) {
      this.state.tried = true;
      this.render();
      const firstInvalid = /** @type {HTMLElement | null} */ (
        this.$('[aria-invalid="true"]') || this.$('[data-ym-error]')
      );
      if (fromSticky || firstInvalid) {
        firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (firstInvalid instanceof HTMLInputElement || firstInvalid instanceof HTMLSelectElement) {
          firstInvalid.focus({ preventScroll: true });
        }
      }
      return;
    }

    /** @type {Record<string, string>} */
    const properties = {};
    if (this.config.personalized && this.state.saved) {
      properties.Name = this.state.saved.name;
      if (this.state.saved.year) properties.Year = this.state.saved.year;
    }
    const setId = this.state.mode === 'set' ? `S${Date.now().toString(36)}` : '';

    const items = this.parts.map((part) => {
      const v = this.variant(part);
      /** @type {Record<string, string>} */
      // The upsell carries no name: it is printed as shown.
      if (part === 'upsell') return { id: v.id, quantity: 1, properties: {} };
      const props = { ...properties };
      if (this.state.saved) Object.assign(props, this.printProps(part, this.state.saved));
      if (this.bundleFrom) props._Bundle = this.bundleFrom;
      if (setId) {
        props._Set = setId;
        props._SetHandle = this.config.handle || '';
        props._Part = part;
      }
      return { id: v.id, quantity: part === 'single' ? this.state.qty : 1, properties: props };
    });

    const added = await this.send(
      JSON.stringify({ items }),
      items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }))
    );
    // The pieces reach the "Yallmark demand" pixel as their own add-to-cart events; the set
    // listing has no cart line of its own, so it gets this custom event.
    if (added && setId) window.Shopify?.analytics?.publish?.('ym_set_added', { productId: String(this.config.productId || '') });
  }

  /**
   * Posts to /cart/add.js, tells the cart drawer and shows the result.
   * @param {string | FormData} body JSON { items } or a product form
   * @param {{ merchandiseId: string, quantity: number }[]} lines
   * @returns {Promise<boolean>} true when the cart took the items
   */
  async send(body, lines) {
    this.state.busy = true;
    this.$$('[data-ym-add]').forEach((b) => b.setAttribute('aria-busy', 'true'));

    let resolve = (/** @type {any} */ _) => {};
    let reject = (/** @type {any} */ _) => {};
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    promise.catch(() => {});

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise,
      })
    );

    const root = window.Shopify?.routes?.root || '/';
    const errorEl = this.$('[data-ym-error]');

    try {
      /** @type {Record<string, string>} */
      const headers = { Accept: 'application/json' };
      if (typeof body === 'string') headers['Content-Type'] = 'application/json';
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers, body });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');

      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'ym-product', itemCount: cart.item_count, didError: false },
      });

      this.state.tried = false;
      this.render();
      const added = this.$('[data-ym-added]');
      if (added instanceof HTMLElement) {
        added.textContent = `Added to cart: ${this.summary}`;
        added.hidden = false;
      }
      // The cart drawer opens itself on the CartLinesUpdateEvent above.
      this.bundleAdded();
      this.offerBundle(cart);
      return true;
    } catch (error) {
      reject(error);
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = error instanceof Error ? error.message : 'Could not add to cart. Try again.';
        errorEl.hidden = false;
      }
      return false;
    } finally {
      this.state.busy = false;
      this.$$('[data-ym-add]').forEach((b) => b.removeAttribute('aria-busy'));
    }
  }

  /* ---------- bundle and save ---------- */

  /** @returns {HTMLDialogElement | null} */
  get bundleDialog() {
    const dialog = this.$('[data-ym-bo]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /**
   * Items in the cart of the products listed in the dialog's data-group (the same kind: this
   * product and its bundle picks) or data-mix-group (the whole list, mix and match).
   * @param {any} cart @param {'group' | 'mixGroup'} [list]
   */
  bundleCount(cart, list = 'group') {
    const group = new Set((this.bundleDialog?.dataset[list] || '').split(',').filter(Boolean));
    return (cart?.items || []).reduce(
      (/** @type {number} */ n, /** @type {any} */ item) => n + (group.has(String(item.product_id)) ? item.quantity : 0),
      0
    );
  }

  /** The offers (custom.bundle_tiers, custom.bundle_mix, from the discounts). */
  get bundleOffer() {
    if (!this._bundleOffer) this._bundleOffer = bundleOffer(this.bundleDialog);
    return this._bundleOffer;
  }

  /** @param {any} cart */
  bundleInCart(cart) {
    return new Set((cart?.items || []).map((/** @type {any} */ item) => String(item.product_id)));
  }

  initBundle() {
    // "See matching items" in Details under the price (snippets/ym-bundle-chip.liquid, which closes
    // itself): the popup, when it has cards
    const see = this.$('[data-ym-bd-see]');
    if (see instanceof HTMLElement) {
      see.hidden = !this.bundleDialog?.querySelector('[data-ym-bo-item]');
      see.addEventListener('click', () => this.openBundleBrowse());
    }

    // Reached from the popup: remember where from for this product (the page may reload), and drop
    // the parameter from the address.
    const key = `ym-bundle:${this.config.productId}`;
    const url = new URL(window.location.href);
    const from = url.searchParams.get('ym_bundle');
    if (from) {
      try {
        sessionStorage.setItem(key, from);
      } catch {}
      url.searchParams.delete('ym_bundle');
      history.replaceState(history.state, '', url);
    }
    try {
      this.bundleFrom = sessionStorage.getItem(key) || '';
    } catch {
      this.bundleFrom = from || '';
    }
    if (!this.bundleFrom) return;

    // The app adds from the product form: the line gets the property from a hidden field.
    const form = this.appForm;
    if (form && !form.querySelector('[name="properties[_Bundle]"]')) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_Bundle]';
      input.value = this.bundleFrom;
      input.dataset.ymBundleInput = '';
      form.append(input);
    }
    if (from) this.openFromBundle();
  }

  /** The popup for the cart as it is now, at any time (the shopper may have closed it after an add). */
  async openBundleBrowse() {
    const dialog = this.bundleDialog;
    if (!dialog || dialog.open) return;
    const root = window.Shopify?.routes?.root || '/';
    let cart = { items: [] };
    try {
      cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    } catch {
      // Without the cart the cards still show, each at its price as a first bundle item.
    }
    const inCart = this.bundleInCart(cart);
    const shown = fillBundleOffer(dialog, {
      count: this.bundleCount(cart),
      mixCount: this.bundleCount(cart, 'mixGroup'),
      inCart,
      pending: !inCart.has(String(this.config.productId)),
    });
    if (shown && !dialog.open) dialog.showModal();
  }

  /** Opens the personalize popup on arrival, unless an option (a size) still has to be chosen. */
  async openFromBundle() {
    if (this.errors.length) return;
    if (this.config.app) {
      // Teeinblue's Personalize button shows some time after the page loads; showApp waits for it
      this.showApp();
      return;
    }
    if (this.config.personalized) this.openPersonalize(/** @type {HTMLElement} */ (this.$('[data-ym-pz-open]')));
  }

  /** After an add from a bundle link: later adds of this product are not counted as from it. */
  bundleAdded() {
    if (!this.bundleFrom) return;
    this.bundleFrom = '';
    try {
      sessionStorage.removeItem(`ym-bundle:${this.config.productId}`);
    } catch {}
    this.appForm?.querySelector('[data-ym-bundle-input]')?.remove();
  }

  /**
   * After an add: "Bundle and save" over the cart drawer, while a bigger discount is still ahead,
   * once per product and number of bundle items in the cart in a session. Cards already in the
   * cart are left out; each shows its price once added.
   * @param {any} cart
   */
  async offerBundle(cart) {
    const dialog = this.bundleDialog;
    if (!dialog || dialog.open) return;
    const count = this.bundleCount(cart);
    const mixCount = this.bundleCount(cart, 'mixGroup');
    if (count < 1 || !bundleNext(this.bundleOffer, count, mixCount)) return;
    const key = `ym-bundle-shown:${this.config.productId}:${count}:${mixCount}`;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch {}

    if (!fillBundleOffer(dialog, { count, mixCount, inCart: this.bundleInCart(cart), skip: 'No thanks, view cart' })) return;
    try {
      sessionStorage.setItem(key, '1');
    } catch {}

    // The cart drawer opens once it has re-rendered; open over it, not under it.
    for (let i = 0; i < 30 && !document.querySelector('[data-ym-cart-dialog][open]'); i += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }
    if (!dialog.open) dialog.showModal();
  }

  /* ---------- app personalizer ---------- */

  /** @returns {HTMLFormElement | null} */
  get appForm() {
    const form = this.$('[data-ym-app-form]');
    return form instanceof HTMLFormElement ? form : null;
  }

  initApp() {
    const form = this.appForm;
    if (!this.config.app || !form) return;

    // Options first: a missing size stops Add to cart, or the app's Personalize button (popup
    // mode, where Add to cart sits in the app's popup), before the app handles the click. On
    // window in the capture phase: Easify takes Add to cart there too (it uploads its preview and
    // print file, then adds by itself) and stops the event; this listener is added first.
    window.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const button = target.closest('[data-ym-add], .tee-btn--personalize');
        if (!button || !(form.contains(button) || button.matches('.tee-btn--personalize'))) return;
        if (this.state.busy) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (!this.errors.length) {
          // The app's Personalize button (Teeinblue popup) goes through: its popup opens.
          if (button.matches('.tee-btn--personalize')) {
            this.publish('personalize_open', { product: this.config.handle || '', parts: ['single'] });
          }
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showAppErrors();
      },
      { capture: true }
    );

    // Listen on document so the app's own submit handler on the form runs first. If the app
    // stops the submit (its fields are not done yet), nothing is sent.
    document.addEventListener('submit', (event) => {
      if (event.target !== form || event.defaultPrevented) return;
      event.preventDefault();
      const data = new FormData(form);
      data.set('quantity', String(this.state.qty));
      const id = String(data.get('id') || '');
      this.send(data, [{ merchandiseId: id, quantity: this.state.qty }]);
    });

    // The app usually adds to cart itself by AJAX (from the page form or its popup) and then fires
    // this event: refresh and open the Yallmark cart drawer, as after a Yallmark add. Teeinblue has
    // no Save of its own: its Add to cart is where personalizing ends (personalize_save).
    document.addEventListener('teeinblue-event-after-cart-added', () => {
      this.publish('personalize_save', { product: this.config.handle || '', parts: ['single'] });
      this.appAdded();
    });

    this.initAppWait();
    this.initAppPopup();
    this.initAppKeyboard();
    this.initAppPreviewRatio();
  }

  /** Marks the missing options and moves to the first one. */
  showAppErrors() {
    this.state.tried = true;
    this.render();
    const firstInvalid = /** @type {HTMLElement | null} */ (this.$('[aria-invalid="true"]'));
    firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    firstInvalid?.focus({ preventScroll: true });
  }

  /** The app's Personalize button (Teeinblue, form in its popup), once it shows. */
  get appTrigger() {
    const button = this.appForm?.querySelector('.tee-btn--personalize');
    return button instanceof HTMLElement && button.getClientRects().length ? button : null;
  }

  /**
   * Teeinblue popup: the Yallmark Personalize button ([data-ym-app-wait], snippets/ym-app-form)
   * stands in until the app's own shows, then hides. If the app puts its form on the page
   * instead (#tee-artwork-form without .teeinblue-widget), Add to cart comes back.
   */
  initAppWait() {
    const wait = /** @type {HTMLElement | null} */ (this.$('[data-ym-app-wait]'));
    const form = this.appForm;
    if (!wait || !form) return;
    const observer = new MutationObserver(() => settle());
    const settle = () => {
      if (this.appTrigger) {
        wait.hidden = true;
      } else if (form.querySelector('#tee-artwork-form:not(.teeinblue-widget)')) {
        wait.hidden = true;
        const add = /** @type {HTMLElement | null} */ (form.querySelector('[data-ym-add]'));
        if (add) add.hidden = false;
      } else {
        return;
      }
      observer.disconnect();
    };
    settle();
    if (!wait.hidden) observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    // Pressed before this script ran (inline onclick in snippets/ym-app-form)
    if (wait.querySelector('[data-ym-pressed]')) this.openAppWhenReady();
  }

  /**
   * Personalize pressed before the app's button shows: options first, as for the app's button,
   * then the app's popup as soon as it can open.
   */
  async openAppWhenReady() {
    const wait = /** @type {HTMLElement | null} */ (this.$('[data-ym-app-wait]'));
    const button = wait?.querySelector('button');
    if (!wait || !button || button.getAttribute('aria-busy') === 'true') return;
    if (this.errors.length) {
      this.showAppErrors();
      return;
    }
    button.setAttribute('aria-busy', 'true');
    for (let i = 0; i < 150 && !this.appTrigger && !wait.hidden; i += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }
    button.removeAttribute('aria-busy');
    if (this.appTrigger || wait.hidden) {
      this.showApp();
      return;
    }
    const errorEl = this.$('[data-ym-error]');
    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = 'Personalization is not available right now. Please try again later.';
      errorEl.hidden = false;
    }
  }

  /**
   * Mobile: the preview in the app's popup (.tee-dialog, Teeinblue) takes the shape of its mockup,
   * so a 4:5 mockup fills the screen width instead of sitting in a square with white bands on the
   * sides. Teeinblue sizes its slides with window.teeinblue.ratio (height / width), read each time
   * it lays out; it is set from the shown mockup's photo once that loads, and is 1 on wider screens,
   * where the popup is laid out around a square (assets/yallmark-teeinblue.css).
   */
  initAppPreviewRatio() {
    const mobile = window.matchMedia('(max-width: 767px)');
    const apply = () => {
      const photo = document.querySelector('.tee-dialog .tee-slide--active .tee-printarea--image img');
      let ratio = 1;
      if (mobile.matches && photo instanceof HTMLImageElement && photo.naturalWidth) {
        ratio = Math.round((photo.naturalHeight / photo.naturalWidth) * 1000) / 1000;
      }
      const options = /** @type {any} */ (window).teeinblue || {};
      if ((options.ratio || 1) === ratio) return;
      options.ratio = ratio;
      /** @type {any} */ (window).teeinblue = options;
      const root = document.documentElement;
      // The app sizes the slides again only when the gallery width changes: narrow it by 1px for
      // one resize, then back.
      root.classList.add('ym-tee-relayout');
      window.dispatchEvent(new Event('resize'));
      window.setTimeout(() => {
        root.classList.remove('ym-tee-relayout');
        window.dispatchEvent(new Event('resize'));
      }, 100);
    };
    // load does not bubble: listen in the capture phase for the mockup photos the app adds
    document.addEventListener(
      'load',
      (event) => {
        if (event.target instanceof HTMLImageElement && event.target.closest('.tee-dialog .tee-slide--active')) apply();
      },
      true
    );
    mobile.addEventListener('change', apply);
  }

  /**
   * Mobile keyboard in the app's popup (.tee-dialog, Teeinblue). iOS pans the page for the keyboard
   * instead of shrinking it, so the sheet follows the visible part of the screen (--ym-vvh / --ym-vvt,
   * used in assets/yallmark-teeinblue.css). The pinned preview gets lower with the visible height while
   * typing and the focused field is scrolled to just below it. Only when the screen left above the
   * keyboard is still too short for both (a small phone) does the preview stop pinning
   * (html.ym-tee-unpin) until the field loses focus.
   */
  initAppKeyboard() {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    const fieldSelector = ".tee-dialog :is(input:not([type='file'], [type='checkbox'], [type='radio']), textarea, select)";
    const sync = () => {
      if (!document.querySelector('.tee-dialog')) {
        root.style.removeProperty('--ym-vvh');
        root.style.removeProperty('--ym-vvt');
        root.classList.remove('ym-tee-unpin', 'ym-tee-typing');
        return;
      }
      const height = `${Math.round(vv.height)}px`;
      const changed = root.style.getPropertyValue('--ym-vvh') !== height;
      root.style.setProperty('--ym-vvh', height);
      root.style.setProperty('--ym-vvt', `${Math.round(vv.offsetTop)}px`);
      // The preview's size follows --ym-vvh on mobile; Teeinblue measures it again only on window
      // resize, which iOS does not fire for the keyboard.
      if (changed && window.matchMedia('(max-width: 767px)').matches) window.dispatchEvent(new Event('resize'));
    };
    /** Brings the focused field into view below the pinned preview, or unpins when there is no room */
    const place = () => {
      const field = document.activeElement;
      if (!(field instanceof HTMLElement) || !field.matches(fieldSelector)) return;
      const body = field.closest('.tee-dialog-body');
      const gallery = body?.querySelector('.editor-gallery-wrapper');
      if (!(body instanceof HTMLElement) || !(gallery instanceof HTMLElement)) {
        field.scrollIntoView({ block: 'center' });
        return;
      }
      const gap = 12;
      const box = body.getBoundingClientRect();
      let visibleBottom = Math.min(box.bottom, vv.offsetTop + vv.height);
      // Shopify's theme preview bar (draft previews only) sits over the bottom of the screen
      const bar = document.getElementById('PBarNextFrameWrapper')?.getBoundingClientRect();
      if (bar && bar.height && bar.top < visibleBottom) visibleBottom = bar.top;
      const pinnedBottom = box.top + gallery.offsetHeight;
      // The whole field, its name above the input too
      const fieldRect = (field.closest('.tee-field') || field).getBoundingClientRect();
      const room = visibleBottom - pinnedBottom - 2 * gap;
      if (room < fieldRect.height) {
        root.classList.add('ym-tee-unpin');
        field.scrollIntoView({ block: 'center' });
        return;
      }
      root.classList.remove('ym-tee-unpin');
      if (fieldRect.top < pinnedBottom + gap) body.scrollTop -= pinnedBottom + gap - fieldRect.top;
      else if (fieldRect.bottom > visibleBottom - gap) body.scrollTop += fieldRect.bottom - (visibleBottom - gap);
    };
    /** @type {number | undefined} */
    let timer;
    const placeSoon = () => {
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        sync();
        place();
      }, 120);
    };
    vv.addEventListener('resize', () => {
      sync();
      placeSoon();
    });
    vv.addEventListener('scroll', sync);
    /** html.ym-tee-typing: a field has focus, the preview may shrink above the keyboard. @param {boolean} on */
    const typing = (on) => {
      if (root.classList.contains('ym-tee-typing') === on) return;
      root.classList.toggle('ym-tee-typing', on);
      if (window.matchMedia('(max-width: 767px)').matches) window.dispatchEvent(new Event('resize'));
    };
    // Teeinblue focuses the first text field when its popup opens, which on a phone brings up the
    // keyboard over the preview before anything is chosen. A field keeps the focus only once the
    // shopper has tapped in the popup, or moves there with the keyboard.
    /** @type {EventTarget | null} */
    let lastPointer = null;
    let lastKey = 0;
    document.addEventListener('pointerdown', (event) => (lastPointer = event.target), true);
    document.addEventListener('keydown', () => (lastKey = Date.now()), true);
    document.addEventListener('focusin', (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.matches(fieldSelector)) return;
      if (
        window.matchMedia('(max-width: 767px)').matches &&
        !(lastPointer instanceof Element && lastPointer.closest('.tee-dialog')) &&
        Date.now() - lastKey > 1000
      ) {
        event.target.blur();
        return;
      }
      typing(true);
      sync();
      // Once the keyboard is up (it takes about 300 ms to open; resize events also call placeSoon)
      window.setTimeout(placeSoon, 350);
    });
    document.addEventListener('focusout', () => {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.matches(fieldSelector)) return;
        root.classList.remove('ym-tee-unpin');
        typing(false);
      }, 0);
    });
  }

  /** After the app added to cart: its popup closes, the cart drawer opens on the CartLinesUpdateEvent. */
  async appAdded() {
    /** @type {HTMLElement | null} */ (document.querySelector('.tee-dialog .tee-dialog-close'))?.click();
    const root = window.Shopify?.routes?.root || '/';
    const v = this.variant('single');
    /** @type {any} */
    let fresh = null;
    const promise = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        fresh = cart;
        return {
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'ym-product', itemCount: cart.item_count, didError: false },
        };
      });
    promise.catch(() => {});
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: v ? [{ merchandiseId: String(v.id), quantity: this.state.qty }] : [],
        promise,
      })
    );
    await promise.catch(() => {});
    this.state.tried = false;
    this.render();
    const added = this.$('[data-ym-added]');
    if (added instanceof HTMLElement) {
      added.textContent = `Added to cart: ${this.summary}`;
      added.hidden = false;
    }
    this.bundleAdded();
    if (fresh) this.offerBundle(fresh);
  }

  /** Keeps the form's variant id and the URL on the chosen variant, for the app to follow. */
  renderApp() {
    if (!this.config.app) return;
    this.renderAppPopup();
    const input = /** @type {HTMLInputElement | null} */ (this.$('[data-ym-app-variant]'));
    const v = this.variant('single');
    if (!input || !v || input.value === String(v.id)) return;
    input.value = String(v.id);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.appForm?.querySelector('[data-variant-id]')?.setAttribute('data-variant-id', String(v.id));
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(v.id));
    history.replaceState(history.state, '', url);
  }

  /** Sticky button: opens the app's popup (popup mode), else brings the app's form into view. */
  showApp() {
    if (this.appDialog) {
      if (this.state.appSaved) /** @type {HTMLElement | null} */ (this.appForm?.querySelector('[data-ym-add]'))?.click();
      else this.openAppPopup(/** @type {HTMLElement | null} */ (this.$('[data-ym-app-start] [data-ym-app-open]')));
      return;
    }
    const trigger = this.appTrigger;
    if (trigger) {
      trigger.click();
      return;
    }
    if (this.$('[data-ym-app-wait]:not([hidden])')) {
      this.openAppWhenReady();
      return;
    }
    const app = /** @type {HTMLElement | null} */ (this.$('#tee-artwork-form') || this.$('[data-ym-app]'));
    if (!app) return;
    app.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const field = /** @type {HTMLElement | null} */ (app.querySelector('input:not([type="hidden"]), select, textarea'));
    field?.focus({ preventScroll: true });
  }

  /* ---------- app in the Yallmark popup ---------- */

  /** @returns {HTMLDialogElement | null} */
  get appDialog() {
    const dialog = this.$('[data-ym-app-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @returns {HTMLInputElement | null} */
  get appCheck() {
    const check = this.appDialog?.querySelector('[data-ym-app-check]');
    return check instanceof HTMLInputElement ? check : null;
  }

  initAppPopup() {
    const dialog = this.appDialog;
    if (!dialog) return;

    dialog.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('[data-ym-app-close]')) dialog.close();
      else if (target.closest('[data-ym-app-save]')) this.saveAppPopup();
    });

    // Editing an option refreshes "What we'll print" and unticks the confirm box.
    /** @param {Event} event */
    const onEdit = (event) => {
      if (!(/** @type {HTMLElement} */ (event.target).closest('[data-ym-app]'))) return;
      const check = this.appCheck;
      if (check) check.checked = false;
      this.toggleAppAckError(false);
      this.renderAppPrint();
    };
    dialog.addEventListener('input', onEdit);
    dialog.addEventListener('change', onEdit);
    this.appCheck?.addEventListener('change', () => {
      if (this.appCheck?.checked) this.toggleAppAckError(false);
    });

    dialog.addEventListener('close', () => {
      // Closed after changing the options without saving: the summary would no longer match.
      if (this.state.appSaved && this.appLines().join('\n') !== this.state.appSaved.join('\n')) this.state.appSaved = null;
      this.renderAppPopup();
      this.appOpener?.focus();
    });
  }

  /** @param {HTMLElement | null} opener */
  openAppPopup(opener) {
    const dialog = this.appDialog;
    if (!dialog || dialog.open) return;
    this.appOpener = opener;
    const hint = dialog.querySelector('[data-ym-app-hint]');
    if (hint) hint.textContent = '';
    this.renderAppPrint();
    dialog.showModal();
    // The app's live preview (Easify) sizes its canvas to the stage when it mounts, while the popup
    // is still closed; a resize makes it measure the stage again now that it shows.
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    this.publish('personalize_open', { product: this.config.handle || '', parts: ['single'] });
  }

  saveAppPopup() {
    const dialog = this.appDialog;
    const check = this.appCheck;
    if (!dialog || !check) return;
    const hint = dialog.querySelector('[data-ym-app-hint]');
    if (!check.checked) {
      this.toggleAppAckError(true);
      check.focus({ preventScroll: true });
      dialog.querySelector('[data-ym-app-ack]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    // The app's own required check; it marks and scrolls to what is missing.
    const validate = /** @type {any} */ (window).TPOConfigs?.actions?.validateOptionSet;
    const drawn = (this.appForm?.querySelector('.easify-product-options')?.childElementCount || 0) > 0;
    if (drawn && typeof validate === 'function' && !validate({ formElement: this.appForm })) {
      if (hint) hint.textContent = 'Fill in the options marked above to save.';
      return;
    }
    if (hint) hint.textContent = '';
    this.state.appSaved = this.appLines();
    this.snapshotAppPreview();
    this.publish('personalize_save', { product: this.config.handle || '', parts: ['single'] });
    dialog.close();
    this.hideAdded();
    requestAnimationFrame(() => /** @type {HTMLElement | null} */ (this.appForm?.querySelector('[data-ym-add]'))?.focus());
  }

  /** The app's live preview, as drawn at Save, becomes the summary card thumbnail. */
  snapshotAppPreview() {
    const thumb = this.$('[data-ym-app-thumb]');
    const canvas = this.appDialog?.querySelector('[data-ym-app-preview] canvas');
    const root = canvas?.closest('[class*="livePreview-root"]');
    if (!thumb || !(canvas instanceof HTMLCanvasElement) || !canvas.width) return;
    if (root instanceof HTMLElement && root.style.display === 'none') return;
    let src = '';
    try {
      src = canvas.toDataURL('image/png');
    } catch {
      return; // A cross-origin layer taints the canvas: keep the product photo.
    }
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    thumb.replaceChildren(img);
  }

  /** Red confirm box with its message. @param {boolean} show */
  toggleAppAckError(show) {
    const dialog = this.appDialog;
    const check = this.appCheck;
    const msg = dialog?.querySelector('[data-ym-app-ack-error]');
    dialog?.querySelector('[data-ym-app-ack]')?.classList.toggle('is-error', show);
    if (msg) msg.textContent = show ? 'Please confirm the details before saving.' : '';
    if (show && msg) {
      check?.setAttribute('aria-describedby', msg.id);
      check?.setAttribute('aria-invalid', 'true');
    } else {
      check?.removeAttribute('aria-describedby');
      check?.removeAttribute('aria-invalid');
    }
  }

  /** The app's visible options as "Label: value" lines, from the form fields it added. */
  appLines() {
    const form = this.appForm;
    if (!form) return [];
    // Easify's live preview image goes in the cart as its own property; it is not printed.
    const previewKey = /** @type {any} */ (window).TPOConfigs?.settings?.livePreviewTextProperties || 'Preview';
    /** @type {string[]} */
    const lines = [];
    for (const [name, value] of new FormData(form).entries()) {
      const label = /^properties\[(.+)\]$/.exec(name)?.[1];
      if (!label || label.startsWith('_') || label === previewKey) continue;
      if (value instanceof File) {
        if (value.name) lines.push(`${label}: ${value.name}`);
      } else if (value.trim()) {
        lines.push(`${label}: ${/^https?:\/\//.test(value) ? 'Uploaded' : value.trim()}`);
      }
    }
    return lines;
  }

  /** @param {string[]} lines */
  appLineRows(lines) {
    return lines.map((line) => {
      const at = line.indexOf(': ');
      const row = document.createElement('div');
      const k = document.createElement('span');
      const v = document.createElement('span');
      k.textContent = `${line.slice(0, at)}: `;
      v.textContent = line.slice(at + 2);
      row.append(k, v);
      return row;
    });
  }

  /** "What we'll print" in the popup. */
  renderAppPrint() {
    const box = /** @type {HTMLElement | null} */ (this.appDialog?.querySelector('[data-ym-app-print]') || null);
    if (!box) return;
    const lines = this.appLines();
    box.hidden = lines.length === 0;
    box.querySelector('[data-ym-app-print-lines]')?.replaceChildren(
      ...this.appLineRows(lines).map((row) => {
        row.className = 'ym-pz__print-line';
        return row;
      })
    );
  }

  /** Personalize button, or the summary card with Edit and Add to cart once saved. */
  renderAppPopup() {
    if (!this.appDialog) return;
    const saved = this.state.appSaved;
    const start = /** @type {HTMLElement | null} */ (this.$('[data-ym-app-start]'));
    const card = /** @type {HTMLElement | null} */ (this.$('[data-ym-app-card]'));
    const add = /** @type {HTMLElement | null} */ (this.appForm?.querySelector('[data-ym-add]') || null);
    if (start) start.hidden = Boolean(saved);
    if (card) card.hidden = !saved;
    if (add) add.hidden = !saved;
    const stickyBtn = this.$('[data-ym-sticky-btn]');
    if (stickyBtn) stickyBtn.textContent = saved ? 'Add to cart' : 'Personalize';
    if (saved && card) card.querySelector('[data-ym-app-lines]')?.replaceChildren(...this.appLineRows(saved));
  }

  /** @param {string} name @param {Record<string, unknown>} data */
  publish(name, data) {
    track(name, data);
  }

  /**
   * Hidden _Print property: the exact print text of one piece, labelled inside a set.
   * @param {string} part @param {{ name: string, year: string }} saved
   */
  printProps(part, saved) {
    const pz = this.pz;
    if (typeof pz?.lines !== 'function') return {};
    const [line] = pz.lines([part], saved.name, saved.year, this.config.milestone || '');
    if (!line) return {};
    return { _Print: this.config.mode === 'set' ? `${line.label}: ${line.text}` : line.text };
  }

  /* ---------- edit from the cart ---------- */

  /** ?ym_edit=<set id or line key>: restore the choices of those cart lines and open the popup. */
  async initEdit() {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('ym_edit');
    if (!id || !this.config.personalized) return;
    url.searchParams.delete('ym_edit');
    history.replaceState(history.state, '', url);

    const root = window.Shopify?.routes?.root || '/';
    /** @type {any} */
    let cart;
    try {
      cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    } catch {
      return;
    }
    const isSet = this.config.mode === 'set';
    const lines = (cart.items || []).filter((/** @type {any} */ item) =>
      isSet ? item.properties?._Set === id : item.key === id
    );
    if (!lines.length) return;

    // What you get
    if (isSet) {
      const parts = lines.map((/** @type {any} */ l) => l.properties?._Part).filter(Boolean);
      const mode = parts.includes('baby') && parts.includes('mama') ? 'set' : parts[0] || 'set';
      /** @type {HTMLElement | null} */ (this.$(`[data-ym-mode="${mode}"]`))?.click();
    }
    // Colour and sizes, from each line's variant options
    for (const line of lines) {
      const part = isSet ? line.properties?._Part : 'single';
      for (const opt of line.options_with_values || []) {
        const name = String(opt.name).toLowerCase();
        if (name === 'color' || name === 'colour') {
          const swatch = this.$$('.ym-swatch').find((el) => /** @type {HTMLElement} */ (el).dataset.ymColor === opt.value);
          if (swatch && swatch.getAttribute('aria-pressed') !== 'true') /** @type {HTMLElement} */ (swatch).click();
          continue;
        }
        const controls = isSet ? this.controls(part) : this.opts;
        const control = controls.find((el) => (el.dataset.ymOptionName || '').toLowerCase() === name);
        if (control instanceof HTMLSelectElement) control.value = opt.value;
        else control?.querySelectorAll('[data-ym-choice]').forEach((b) => {
          b.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (b).dataset.ymChoice === opt.value));
        });
      }
    }
    if (!isSet && lines[0].quantity) this.state.qty = lines[0].quantity;

    const props = lines[0].properties || {};
    this.state.saved = { name: props.Name || '', year: props.Year || String(this.config.year || '') };
    this.state.editing = lines.map((/** @type {any} */ l) => ({
      key: l.key,
      quantity: l.quantity,
      part: isSet ? l.properties?._Part || 'baby' : 'single',
      properties: l.properties || {},
    }));
    this.render();

    await customElements.whenDefined('ym-personalize');
    this.openPersonalize(/** @type {HTMLElement} */ (this.$('[data-ym-pz-card] [data-ym-pz-open]')));
  }

  /**
   * Save an edited name onto the cart lines it came from, instead of adding new ones.
   * @param {{ name: string, year: string }} saved
   */
  async updateCartPersonalization(saved) {
    const editing = /** @type {{ key: string, quantity: number, part: string, properties: Record<string, string> }[]} */ (
      this.state.editing
    );
    this.state.editing = null;
    this.render();

    let resolve = (/** @type {any} */ _) => {};
    let reject = (/** @type {any} */ _) => {};
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    promise.catch(() => {});
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'product',
        lines: editing.map((line) => ({ lineKey: line.key, quantity: line.quantity })),
        promise,
      })
    );

    const root = window.Shopify?.routes?.root || '/';
    const added = this.$('[data-ym-added]');
    const errorEl = this.$('[data-ym-error]');
    try {
      /** @type {any} */
      const drawer = document.querySelector('ym-cart-drawer');
      const sectionId = drawer?.sectionId;
      // One line at a time: changing a line's properties changes only that line's key. The last
      // call also brings back the drawer's new HTML, so it never shows the old name.
      for (const [i, line] of editing.entries()) {
        const properties = { ...line.properties, Name: saved.name, ...this.printProps(line.part, saved) };
        if (saved.year) properties.Year = saved.year;
        const last = i === editing.length - 1;
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties, ...(last && sectionId ? { sections: sectionId } : {}) }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not update the cart');
        if (last && sectionId && data.sections?.[sectionId]) drawer.render(data.sections[sectionId]);
      }
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'ym-product', itemCount: cart.item_count, didError: false },
      });
      if (added instanceof HTMLElement) {
        added.textContent = 'Personalization updated in your cart.';
        added.hidden = false;
      }
      drawer?.open?.();
    } catch (error) {
      reject(error);
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = error instanceof Error ? error.message : 'Could not update your cart. Try again.';
        errorEl.hidden = false;
      }
    }
  }

  /* ---------- size chart ---------- */

  initSizeChart() {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.$('[data-ym-sc]'));
    if (!dialog) return;
    this.sc = dialog;
    /** @type {{ tab: string, pick: Record<string, string>, unit: string, msg: string }} */
    this.scState = { tab: '', pick: {}, unit: 'in', msg: '' };

    dialog.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      // A click on the dialog box itself, outside its content, is a click on the backdrop.
      if (target === dialog || target.closest('[data-ym-sc-close]')) {
        dialog.close();
        return;
      }
      const tab = target.closest('[data-ym-sc-tab]');
      if (tab instanceof HTMLElement) {
        this.scState.tab = tab.dataset.ymScTab || '';
        this.scState.msg = '';
        this.renderSizeChart();
        return;
      }
      const unit = target.closest('[data-ym-sc-unit]');
      if (unit instanceof HTMLElement) {
        this.scState.unit = unit.dataset.ymScUnit || 'in';
        this.renderSizeChart();
        return;
      }
      const row = target.closest('[data-ym-sc-size]');
      if (row instanceof HTMLElement) {
        if (row.getAttribute('aria-disabled') === 'true') return;
        this.scState.pick[this.scState.tab] = row.dataset.ymScSize || '';
        this.scState.msg = '';
        this.renderSizeChart();
        return;
      }
      if (target.closest('[data-ym-sc-choose]')) this.chooseSize();
    });

    dialog.addEventListener('close', () => {
      this.scOpener?.focus({ preventScroll: true });
    });
  }

  /** Lower-case, en dashes to hyphens, no spaces, so "0–3M" matches "0-3m". @param {string} value */
  static sizeKey(value) {
    return value.toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, '');
  }

  /** Chart forms in play: the chart's forms limited to the garments of the chosen "What you get". */
  get scForms() {
    const forms = (this.sc?.dataset.forms || '').split(',').filter(Boolean);
    if (this.config.mode !== 'set') return forms;
    return forms.filter((form) => this.parts.includes(form));
  }

  /** The size control on the page that a chart form fills. @param {string} form */
  scControl(form) {
    if (this.config.mode === 'set') return /** @type {HTMLElement | null} */ (this.$(`[data-ym-size="${form}"]`));
    return this.opts.find((el) => /size/i.test(el.dataset.ymOptionName || '')) || this.opts[0] || null;
  }

  /** Values the page control offers. @param {HTMLElement} control */
  scValues(control) {
    if (control instanceof HTMLSelectElement) {
      return Array.from(control.options).map((o) => o.value).filter(Boolean);
    }
    return Array.from(control.querySelectorAll('[data-ym-choice]')).map(
      (el) => /** @type {HTMLElement} */ (el).dataset.ymChoice || ''
    );
  }

  /** Page value for a chart size, or '' when the product does not offer it. @param {string} form @param {string} size */
  scMatch(form, size) {
    const control = this.scControl(form);
    if (!control) return '';
    const key = YmProduct.sizeKey(size);
    return this.scValues(control).find((v) => YmProduct.sizeKey(v) === key) || '';
  }

  /** Chart size matching what is chosen on the page for a form. @param {string} form */
  scPageSize(form) {
    const control = this.scControl(form);
    const value = control ? this.optValue(control) : '';
    if (!value || !this.sc) return '';
    const key = YmProduct.sizeKey(value);
    const row = /** @type {HTMLElement[]} */ (this.$$(`[data-ym-sc-panel="${form}"] [data-ym-sc-size]`)).find(
      (el) => YmProduct.sizeKey(el.dataset.ymScSize || '') === key
    );
    return row?.dataset.ymScSize || '';
  }

  /** @param {HTMLElement} opener */
  openSizeChart(opener) {
    const dialog = this.sc;
    const forms = this.scForms;
    if (!dialog || !forms.length) return;
    const hasValue = (/** @type {string} */ form) => {
      const control = this.scControl(form);
      return Boolean(control && this.optValue(control));
    };
    // Open on the size being chosen: baby first in a set, unless it is done and the other is not.
    let tab = forms.includes('baby') && this.state.mode !== 'mama' ? 'baby' : forms[0];
    if (hasValue(tab)) tab = forms.find((form) => !hasValue(form)) || tab;
    this.scState.tab = tab;
    this.scState.msg = '';
    this.scState.pick = {};
    for (const form of forms) this.scState.pick[form] = this.scPageSize(form);
    this.scOpener = opener;
    this.renderSizeChart();
    dialog.showModal();
    /** @type {HTMLElement | null} */ (dialog.querySelector(`[data-ym-sc-tab="${tab}"]`))?.focus();
  }

  renderSizeChart() {
    const dialog = this.sc;
    if (!dialog) return;
    const { tab, pick, unit, msg } = this.scState;
    const forms = this.scForms;
    const both = forms.length > 1;

    dialog.classList.toggle('is-cm', unit === 'cm');
    dialog.querySelectorAll('[data-ym-sc-unit]').forEach((b) => {
      b.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (b).dataset.ymScUnit === unit));
    });

    const tabs = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-ym-sc-tabs]'));
    if (tabs) tabs.hidden = !both;
    dialog.querySelectorAll('[data-ym-sc-tab]').forEach((el) => {
      const button = /** @type {HTMLElement} */ (el);
      const form = button.dataset.ymScTab || '';
      const on = form === tab;
      button.hidden = !forms.includes(form);
      button.setAttribute('aria-selected', String(on));
      button.setAttribute('tabindex', on ? '0' : '-1');
      const done = button.querySelector('[data-ym-sc-done]');
      const value = this.scPageSize(form);
      if (done) done.textContent = value ? `· ${value}` : '';
    });

    let missing = false;
    dialog.querySelectorAll('[data-ym-sc-panel]').forEach((el) => {
      const panel = /** @type {HTMLElement} */ (el);
      const form = panel.dataset.ymScPanel || '';
      panel.hidden = form !== tab;
      if (form !== tab) return;
      const rows = /** @type {HTMLElement[]} */ (Array.from(panel.querySelectorAll('[data-ym-sc-size]')));
      rows.forEach((row) => {
        const size = row.dataset.ymScSize || '';
        const offered = Boolean(this.scMatch(form, size));
        if (!offered) missing = true;
        row.setAttribute('aria-disabled', String(!offered));
        row.setAttribute('aria-checked', String(offered && size === pick[form]));
      });
      // Roving focus: the checked row, else the first one on offer.
      const focusRow =
        rows.find((row) => row.getAttribute('aria-checked') === 'true') ||
        rows.find((row) => row.getAttribute('aria-disabled') !== 'true');
      rows.forEach((row) => row.setAttribute('tabindex', row === focusRow ? '0' : '-1'));
      const missingNote = /** @type {HTMLElement | null} */ (panel.querySelector('[data-ym-sc-missing]'));
      if (missingNote) missingNote.hidden = !missing;
      const msgEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-ym-sc-msg]'));
      if (msgEl) {
        msgEl.hidden = !msg;
        msgEl.textContent = msg;
      }
    });

    const choose = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-ym-sc-choose]'));
    if (choose) {
      const size = pick[tab] && this.scMatch(tab, pick[tab]) ? pick[tab] : '';
      choose.setAttribute('aria-disabled', String(!size));
      choose.textContent = size ? `Select ${both ? `${tab} size ` : ''}${size}` : 'Choose a size above';
    }
  }

  chooseSize() {
    const dialog = this.sc;
    if (!dialog) return;
    const { tab, pick } = this.scState;
    const size = pick[tab];
    const value = size ? this.scMatch(tab, size) : '';
    const control = this.scControl(tab);
    if (!value || !control) return;

    if (control instanceof HTMLSelectElement) {
      control.value = value;
    } else {
      control.querySelectorAll('[data-ym-choice]').forEach((b) => {
        b.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (b).dataset.ymChoice === value));
      });
    }
    this.hideAdded();
    this.render();

    const next = this.scForms.find((form) => {
      if (form === tab) return false;
      const other = this.scControl(form);
      return !(other && this.optValue(other));
    });
    if (next) {
      const panel = /** @type {HTMLElement | null} */ (dialog.querySelector(`[data-ym-sc-panel="${tab}"]`));
      const name = panel?.dataset.name || 'Size';
      this.scState.tab = next;
      this.scState.msg = `${name} set to ${size}. Now choose the ${next} size.`;
      this.renderSizeChart();
      /** @type {HTMLElement | null} */ (dialog.querySelector(`[data-ym-sc-tab="${next}"]`))?.focus();
    } else {
      dialog.close();
    }
  }

  /* ---------- sticky bar ---------- */

  initSticky() {
    const sticky = /** @type {HTMLElement | null} */ (this.$('[data-ym-sticky]'));
    const main = this.$('[data-ym-buy]');
    if (!sticky || !main || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.hidden = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(main);
  }

  /* ---------- gallery ---------- */

  initGallery() {
    const list = /** @type {HTMLElement | null} */ (this.$('[data-ym-gallery-list]'));
    if (!list) return;
    const slides = this.$$('[data-ym-slide]');
    const thumbs = this.$$('[data-ym-thumb]');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.setGalleryCurrent(slides.indexOf(/** @type {HTMLElement} */ (entry.target)));
          }
        },
        { root: list, threshold: 0.6 }
      );
      slides.forEach((s) => observer.observe(s));
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const i = Number(/** @type {HTMLElement} */ (thumb).dataset.ymThumb);
        const slide = /** @type {HTMLElement | undefined} */ (slides[i]);
        if (!slide) return;
        list.scrollTo({ left: slide.offsetLeft - list.offsetLeft, behavior: 'smooth' });
        this.setGalleryCurrent(i);
      });
    });
  }

  /** Counter and thumbnail of the slide in view (mobile). @param {number} i */
  setGalleryCurrent(i) {
    const index = this.$('[data-ym-gallery-index]');
    if (index) index.textContent = String(i + 1);
    this.$$('[data-ym-thumb]').forEach((t, n) => {
      if (n === i) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  }

  /** On load, shows the photo of the ?variant= in the URL; from then on render() follows the choices. */
  initVariantMedia() {
    const id = new URL(window.location.href).searchParams.get('variant');
    const variant = id && this.product('single')?.variants.find((/** @type {any} */ v) => String(v.id) === id);
    this.galleryMedia = '';
    this.showVariantMedia(variant || null, true);
  }

  /** Variant whose photo the gallery should show: the exact one, else one standing for all matching variants when they share a photo. */
  mediaVariant() {
    if (this.state.mode !== 'single') return null;
    const exact = this.variant('single');
    if (exact) return exact;
    const pool = this.matching('single');
    return pool.length && pool.every((/** @type {any} */ v) => v.media === pool[0].media) ? pool[0] : null;
  }

  /**
   * Brings the variant's featured media into view. A variant without one, a photo every variant
   * shares (sizes of a tee) or the photo already shown leaves the gallery alone.
   * @param {any} variant
   * @param {boolean} [instant]
   */
  showVariantMedia(variant, instant = false) {
    const id = variant?.media ? String(variant.media) : '';
    const variants = this.product('single')?.variants || [];
    if (!id || id === this.galleryMedia || variants.every((/** @type {any} */ v) => String(v.media) === id)) return;
    const list = /** @type {HTMLElement | null} */ (this.$('[data-ym-gallery-list]'));
    const slides = /** @type {HTMLElement[]} */ (this.$$('[data-ym-slide]'));
    const slide = slides.find((s) => s.dataset.mediaId === id);
    if (!list || !slide) return;
    this.galleryMedia = id;

    // Desktop: the photo takes the full-width spot at the top (CSS order); the badge goes with it.
    const lead = slide !== slides[0];
    slides.forEach((s) => s.classList.toggle('ym-gal__slide--lead', lead && s === slide));
    list.classList.toggle('ym-gal__list--lead', lead);
    const badge = list.querySelector('.ym-gal__badge');
    if (badge) slide.append(badge);

    // Mobile: scroll the strip to it (no-op on desktop, where the list does not scroll).
    list.scrollTo({ left: slide.offsetLeft - list.offsetLeft, behavior: instant ? 'auto' : 'smooth' });
    this.setGalleryCurrent(slides.indexOf(slide));
  }
}

if (!customElements.get('ym-product')) customElements.define('ym-product', YmProduct);
